import { useState, useEffect, useMemo } from "react";
import { API_BASE_URL } from "./constants";

export async function updateProgress(user, unit, textbook, game, is_correct) {
  // fetch data
  let data = {
    user: user,
    unit: unit,
    textbook: textbook,
    game: game,
    is_correct: is_correct,
  };

  try {
    const response = await fetch(`${API_BASE_URL}/update_progress`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (response.ok) {
      console.log("Updated progress:", result);
    } else {
      console.error("Error submitting feedback:", result.error);
    }
  } catch (error) {
    console.error("Network error:", error);
  }
}

export function useGrammarUnitData(unitNumber, textbook) {
  // return GRAMMAR[unitNumber];
  const [grammarData, setGrammarData] = useState([]);
  const [choicesData, setChoicesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // fetch data
  useEffect(() => {
    if (!unitNumber || !textbook) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const grammarUrl = `${API_BASE_URL}/grammar?unit=${unitNumber}&textbook=${textbook}`;
        console.log("Grammar URL:", grammarUrl);
        const grammarResponse = await fetch(grammarUrl);
        if (!grammarResponse.ok)
          throw new Error(`HTTP error! status: ${grammarResponse.status}`);
        const grammarJson = await grammarResponse.json();
        console.log(grammarJson);

        setGrammarData((prevData) =>
          JSON.stringify(prevData) !== JSON.stringify(grammarJson)
            ? grammarJson
            : prevData,
        );

        const choicesResponse = await fetch(
          `${API_BASE_URL}/choices?unit=${unitNumber}&textbook=${textbook}`,
        );
        if (!choicesResponse.ok)
          throw new Error(`HTTP error! status: ${choicesResponse.status}`);
        const choicesJson = await choicesResponse.json();
        console.log(choicesJson);

        setChoicesData((prevChoices) =>
          JSON.stringify(prevChoices) !== JSON.stringify(choicesJson)
            ? choicesJson
            : prevChoices,
        );
      } catch (error) {
        setError(error.message);
        console.log("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [unitNumber, textbook]);

  // grammar data should have the different questions for the unit,
  // choices data should have choices for questions with multiple choice

  const choicesMap = useMemo(() => 
    choicesData.reduce((acc, choice) => {
      if (!acc[choice.question_id]) {
        acc[choice.question_id] = [];
      }
      acc[choice.question_id].push({
        text: choice.choice_text,
        isCorrect: choice.is_correct === 1,
      });
      return acc;
    }, {}),
    [choicesData]
  );

  const grammarWithChoices = useMemo(() => 
    grammarData.map((q) => ({
      ...q,
      choices: choicesMap[q.id] || [],
    })),
    [grammarData, choicesMap]
  );
  console.log(grammarWithChoices);

  return { data: grammarWithChoices, loading, error };
}

export function useListeningUnitData(unitNumber, textbook) {
  const [listeningQuestionsData, setListeningQuestionsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // fetch data
  useEffect(() => {
    if (!unitNumber || !textbook) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const listeningQuestionsResponse = await fetch(
          `${API_BASE_URL}/listening_questions?unit=${unitNumber}&textbook=${textbook}`
        );
        if (!listeningQuestionsResponse.ok)
          throw new Error(`HTTP error! status: ${listeningQuestionsResponse.status}`);

        const listeningQuestionsJson = await listeningQuestionsResponse.json();

        const listeningOptionsResponse = await fetch(
          `${API_BASE_URL}/listening_question_options?unit=${unitNumber}&textbook=${textbook}`
        );
        if (!listeningOptionsResponse.ok)
          throw new Error(`HTTP error! status: ${listeningOptionsResponse.status}`);

        const listeningOptionsJson = await listeningOptionsResponse.json();

        const questionsMap = {};

        listeningQuestionsJson.forEach(({ question_number, audio_path }) => {
          questionsMap[question_number] = { options: [], audio_path: audio_path };
        });

        listeningOptionsJson.forEach(({ question_number, answer, is_correct }) => {
          questionsMap[question_number].options.push({ answer : answer, is_correct: is_correct });
        });

        setListeningQuestionsData(questionsMap);
      } catch (error) {
        setError(error.message);
        console.log("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [unitNumber, textbook]);

  // process pure data stored
  return { data: listeningQuestionsData, loading, error };
}

export function useVocabUnitData(unitNumber, textbook) {
  const [vocabData, setVocabData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!unitNumber || !textbook) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const vocabResponse = await fetch(
          `${API_BASE_URL}/vocab?unit=${unitNumber}&textbook=${textbook}`,
        );
        if (!vocabResponse.ok) {
          throw new Error(`HTTP error! status: ${vocabResponse.status}`);
        }
        const vocabJson = await vocabResponse.json();
        console.log(vocabJson);

        const vocabLiteralsResponse = await fetch(
          `${API_BASE_URL}/vocabl?unit=${unitNumber}&textbook=${textbook}`,
        );
        if (!vocabLiteralsResponse.ok) {
          throw new Error(
            `HTTP error! status: ${vocabLiteralsResponse.status}`,
          );
        }
        const vocabLiteralsJson = await vocabLiteralsResponse.json();

        const literalsMap = {};
        vocabLiteralsJson.forEach(({ vocab_id, literal }) => {
          if (!literalsMap[vocab_id]) {
            literalsMap[vocab_id] = [];
          }
          literalsMap[vocab_id].push(literal);
        });

        const mergedData = vocabJson.map((v) => ({
          korean: v.korean,
          imgPath: v.img_path,
          imgWidth: v.img_width,
          imgHeight: v.img_height,
          audioPath: v.audio_path,
          englishLiterals: literalsMap[v.id] || [],
        }));

        setVocabData(mergedData);
      } catch (error) {
        setError(error.message);
        console.log("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [unitNumber, textbook]);

  return { data: vocabData, loading, error };
}

export function useUnitData(textbook) {
  const [unitData, setUnitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!textbook) return;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const unitResponse = await fetch(
          `${API_BASE_URL}/units?textbook=${textbook}`
        );
        if (!unitResponse.ok) {
          throw new Error(`HTTP error! status: ${unitResponse.status}`);
        }
        const unitJson = await unitResponse.json();
        setUnitData(unitJson);
      } catch (error) {
        setError(error.message);
        console.log("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [textbook]);

  return { data: unitData, loading, error };
}
